import { Controller } from "@hotwired/stimulus"

/**
 * Match-Filter: einheitliche Submit-Logik (GET → turbo-frame matches)
 *
 * - Schnellfilter-Segmente (Person, Score-Preset, Aktivität) + Ähnlichkeit + Verfügbarkeit:
 *   nach Klick sofort (debounced 300 ms) submitten.
 * - Range-Slider: während Ziehens nur Werte spiegeln; Submit erst bei change (Loslassen),
 *   damit nicht während des Drags gesucht wird.
 * - Zahlfelder, Stadt (change): debounced Submit; Sortierung per Segment (Bereich Optionen).
 */
export default class extends Controller {
  static targets = [
    "form",
    "minScore",
    "minScoreNumber",
    "minCulturalSlider",
    "minCulturalNumber",
    "personTypeHidden",
    "personSegment",
    "scorePresetSegment",
    "lastActivityHidden",
    "lastActivitySegment",
    "similarityModeHidden",
    "similaritySegment",
    "availableOnlyCheckbox",
    "availableOnlySwitch",
    "availableOnlySwitchIndicator",
    "city",
    "sortByHidden",
    "sortBySegment",
    "resultCount"
  ]

  connect() {
    this.refreshPersonSegmentStyles()
    this.refreshScorePresetStyles()
    this.refreshLastActivityStyles()
    this.refreshSimilaritySegmentStyles()
    this.refreshSortBySegmentStyles()
    this.refreshAvailableOnlySwitchUi()
    this.updateResultCount()
  }

  toggleAvailableOnly(event) {
    event?.preventDefault()
    if (!this.hasAvailableOnlyCheckboxTarget) return
    const cb = this.availableOnlyCheckboxTarget
    cb.checked = !cb.checked
    this.refreshAvailableOnlySwitchUi()
    this.scheduleSubmit()
  }

  onAvailableOnlyCheckboxChange() {
    this.refreshAvailableOnlySwitchUi()
    this.scheduleSubmit()
  }

  refreshAvailableOnlySwitchUi() {
    if (!this.hasAvailableOnlyCheckboxTarget) return
    const on = this.availableOnlyCheckboxTarget.checked
    if (this.hasAvailableOnlySwitchTarget) {
      this.availableOnlySwitchTarget.setAttribute("aria-checked", String(on))
      this.availableOnlySwitchTarget.classList.toggle("bg-blue-600", on)
      this.availableOnlySwitchTarget.classList.toggle("bg-gray-200", !on)
    }
    if (this.hasAvailableOnlySwitchIndicatorTarget) {
      this.availableOnlySwitchIndicatorTarget.classList.toggle("translate-x-5", on)
      this.availableOnlySwitchIndicatorTarget.classList.toggle("translate-x-0", !on)
    }
  }

  setSortBy(event) {
    const value = event.currentTarget.dataset.sortByValue
    if (this.hasSortByHiddenTarget && value) {
      this.sortByHiddenTarget.value = value
    }
    this.refreshSortBySegmentStyles()
    this.scheduleSubmit()
  }

  refreshSortBySegmentStyles() {
    if (!this.hasSortByHiddenTarget || !this.hasSortBySegmentTarget) return
    const current = this.sortByHiddenTarget.value
    this.sortBySegmentTargets.forEach((el) => {
      const isActive = el.dataset.sortByValue === current
      el.classList.toggle("bg-blue-100", isActive)
      el.classList.toggle("border-blue-300", isActive)
      el.classList.toggle("text-blue-800", isActive)
      el.classList.toggle("z-10", isActive)
      el.classList.toggle("bg-white", !isActive)
      el.classList.toggle("text-gray-700", !isActive)
    })
  }

  setSimilarityMode(event) {
    const value = event.currentTarget.dataset.similarityModeValue
    if (this.hasSimilarityModeHiddenTarget && value) {
      this.similarityModeHiddenTarget.value = value
    }
    this.refreshSimilaritySegmentStyles()
    this.scheduleSubmit()
  }

  refreshSimilaritySegmentStyles() {
    if (!this.hasSimilarityModeHiddenTarget || !this.hasSimilaritySegmentTarget) return
    const current = this.similarityModeHiddenTarget.value
    this.similaritySegmentTargets.forEach((el) => {
      const isActive = el.dataset.similarityModeValue === current
      el.classList.toggle("bg-blue-100", isActive)
      el.classList.toggle("border-blue-300", isActive)
      el.classList.toggle("text-blue-800", isActive)
      el.classList.toggle("z-10", isActive)
      el.classList.toggle("bg-white", !isActive)
      el.classList.toggle("text-gray-700", !isActive)
    })
  }

  setPersonType(event) {
    const value = event.currentTarget.dataset.personTypeValue
    if (this.hasPersonTypeHiddenTarget) {
      this.personTypeHiddenTarget.value = value
    }
    this.refreshPersonSegmentStyles()
    this.scheduleSubmit()
  }

  setScorePreset(event) {
    const v = event.currentTarget.dataset.minScoreValue
    if (this.hasMinScoreTarget && this.hasMinScoreNumberTarget) {
      this.minScoreTarget.value = v
      this.minScoreNumberTarget.value = v
    }
    this.refreshScorePresetStyles()
    this.scheduleSubmit()
  }

  refreshPersonSegmentStyles() {
    if (!this.hasPersonTypeHiddenTarget || !this.hasPersonSegmentTarget) return
    const current = this.personTypeHiddenTarget.value
    this.personSegmentTargets.forEach((el) => {
      const isActive = el.dataset.personTypeValue === current
      el.classList.toggle("bg-blue-100", isActive)
      el.classList.toggle("border-blue-300", isActive)
      el.classList.toggle("text-blue-800", isActive)
      el.classList.toggle("z-10", isActive)
      el.classList.toggle("bg-white", !isActive)
      el.classList.toggle("text-gray-700", !isActive)
    })
  }

  refreshScorePresetStyles() {
    if (!this.hasMinScoreTarget || !this.hasScorePresetSegmentTarget) return
    const cur = parseInt(this.minScoreTarget.value, 10) || 0
    const high = cur >= 80
    this.scorePresetSegmentTargets.forEach((el) => {
      const preset = parseInt(el.dataset.minScoreValue, 10)
      const isActive = (preset === 80 && high) || (preset === 0 && !high)
      el.classList.toggle("bg-blue-100", isActive)
      el.classList.toggle("border-blue-300", isActive)
      el.classList.toggle("text-blue-800", isActive)
      el.classList.toggle("z-10", isActive)
      el.classList.toggle("bg-white", !isActive)
      el.classList.toggle("text-gray-700", !isActive)
    })
  }

  setLastActivity(event) {
    const value = event.currentTarget.dataset.lastActivityValue
    if (this.hasLastActivityHiddenTarget) {
      this.lastActivityHiddenTarget.value = value
    }
    this.refreshLastActivityStyles()
    this.scheduleSubmit()
  }

  refreshLastActivityStyles() {
    if (!this.hasLastActivityHiddenTarget || !this.hasLastActivitySegmentTarget) return
    const current = this.lastActivityHiddenTarget.value
    this.lastActivitySegmentTargets.forEach((el) => {
      const isActive = el.dataset.lastActivityValue === current
      el.classList.toggle("bg-blue-100", isActive)
      el.classList.toggle("border-blue-300", isActive)
      el.classList.toggle("text-blue-800", isActive)
      el.classList.toggle("z-10", isActive)
      el.classList.toggle("bg-white", !isActive)
      el.classList.toggle("text-gray-700", !isActive)
    })
  }

  filterChanged(event) {
    const t = event && event.target

    const isMinScoreRange =
      this.hasMinScoreTarget && t === this.minScoreTarget
    const isCulturalRange =
      this.hasMinCulturalSliderTarget && t === this.minCulturalSliderTarget
    const isRangeDragging =
      event?.type === "input" && (isMinScoreRange || isCulturalRange)

    if (this.hasMinScoreTarget && this.hasMinScoreNumberTarget) {
      if (t === this.minScoreNumberTarget) {
        this.minScoreTarget.value = this.minScoreNumberTarget.value
      } else if (t === this.minScoreTarget) {
        this.minScoreNumberTarget.value = this.minScoreTarget.value
      } else {
        this.minScoreNumberTarget.value = this.minScoreTarget.value
      }
    }

    if (this.hasMinCulturalSliderTarget && this.hasMinCulturalNumberTarget) {
      if (t === this.minCulturalNumberTarget) {
        this.minCulturalSliderTarget.value = this.minCulturalNumberTarget.value
      } else if (t === this.minCulturalSliderTarget) {
        this.minCulturalNumberTarget.value = this.minCulturalSliderTarget.value
      }
    }

    this.refreshScorePresetStyles()

    if (isRangeDragging) return

    this.scheduleSubmit()
  }

  scheduleSubmit() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => {
      this.submitForm()
    }, 300)
  }

  validateScoreRanges() {
    const minScore = parseInt(this.minScoreTarget.value, 10) || 0

    if (minScore < 0) {
      this.minScoreTarget.value = 0
      this.minScoreNumberTarget.value = 0
    }

    if (minScore > 100) {
      this.minScoreTarget.value = 100
      this.minScoreNumberTarget.value = 100
    }
    this.refreshScorePresetStyles()
  }

  validateCulturalScoreRanges() {
    if (!this.hasMinCulturalSliderTarget || !this.hasMinCulturalNumberTarget) return
    const raw = this.minCulturalNumberTarget.value.trim()
    if (raw === "") {
      this.minCulturalSliderTarget.value = "0"
      return
    }
    let v = parseInt(raw, 10) || 0
    if (v < 0) v = 0
    if (v > 100) v = 100
    this.minCulturalSliderTarget.value = String(v)
    this.minCulturalNumberTarget.value = String(v)
  }

  submitForm() {
    const form = this.resolveFormElement()
    if (form) {
      form.requestSubmit()
    }
  }

  /** Formular zuverlässig finden (Target kann in Edge-Cases fehlen; Controller sitzt auf dem Wrapper um das Formular). */
  resolveFormElement() {
    const root = this.element
    if (root instanceof HTMLFormElement) {
      return root
    }
    if (this.hasFormTarget) {
      return this.formTarget
    }
    return root.querySelector("form")
  }

  updateResultCount() {
    // Platzhalter für spätere Live-Anzeige
  }
}
